<!--
  CLIENTI — anagrafica e pacchetti valet, sul modello dell'admin.

  - UNA tabella e tre riquadri che contano e filtrano insieme: Clienti
    (privati), Aziende (con ragione sociale) e Valet (tutti i pacchetti del
    partner, dal più recente).
  - Sulla riga del cliente c'è lo stato del suo pacchetto, con i residui.
  - «Da confermare» si chiama «In attesa di Simba»: il pacchetto proposto dal
    partner lo accetta Simba. Al cliente non parte più nessuna email.
  - Pagine chieste al server, 25 righe per volta, e ricerca dal database.
-->
<template>
  <div class="cst">
    <!-- ---------- intestazione ---------- -->
    <div class="cst__head">
      <div>
        <h1 class="cst__title">
          Clienti
        </h1>
        <p class="cst__sub">
          Anagrafica dei tuoi clienti e dei loro pacchetti valet.
        </p>
      </div>
      <div class="cst__spacer" />
      <el-input
        v-model="cerca"
        class="cst__search"
        :placeholder="segnaposto"
        clearable
        @input="onCerca"
        @clear="onCerca"
      />
      <el-button
        :loading="esporto"
        @click="scaricaExcel"
      >
        Scarica Excel
      </el-button>
      <router-link
        to="/addcustomer_employee"
        class="routerLink"
      >
        <el-button type="primary">
          Nuovo cliente
        </el-button>
      </router-link>
    </div>

    <!-- ---------- riquadri: contano e filtrano ---------- -->
    <div
      class="cst__kpis"
      role="tablist"
      aria-label="Filtra i clienti"
    >
      <button
        v-for="k in riquadri"
        :key="k.key"
        type="button"
        role="tab"
        class="cst__kpi"
        :class="[`cst__kpi--${k.tono}`, { 'is-on': segmento === k.key }]"
        :aria-selected="segmento === k.key ? 'true' : 'false'"
        @click="scegli(k.key)"
      >
        <span class="cst__kpiN">
          {{ k.n === null ? '—' : k.n }}
          <!-- Il numero grande è QUANTI SONO; quello piccolo quanti chiedono
               un'azione. Prima il riquadro contava solo questi ultimi, e un
               partner con tutti i pacchetti confermati leggeva «0 valet». -->
          <small
            v-if="k.badge"
            class="cst__kpiB"
          >{{ k.badge }}</small>
        </span>
        <span class="cst__kpiL">{{ k.label }}</span>
        <span class="cst__kpiH">{{ k.hint }}</span>
      </button>
    </div>

    <!-- ---------- tabella ---------- -->
    <div class="cst__card">
      <div class="cst__bar">
        <span class="cst__barT">{{ titoloTabella }}</span>
        <div class="cst__spacer" />
        <span class="cst__barH">{{ notaTabella }}</span>
      </div>

      <!-- Un errore si dice: senza questa riga una rete caduta sembrerebbe
           «nessun cliente». -->
      <div
        v-if="errore"
        class="cst__errore"
      >
        {{ errore }}
        <button
          type="button"
          class="cst__riprova"
          @click="ricarica"
        >
          Riprova
        </button>
      </div>

      <el-table
        :key="segmento"
        v-loading="caricamento"
        :data="righe"
        style="width: 100%"
        :empty-text="testoVuoto"
        @row-click="apri"
      >
        <!-- ==== ANAGRAFICA (clienti privati e aziende) ==== -->
        <template v-if="segmento !== 'attesa'">
          <el-table-column
            label="Cliente"
            min-width="260"
          >
            <template #default="{ row }">
              <div class="cst__who">
                <span class="cst__avatar">{{ iniziali(row) }}</span>
                <div class="cst__whoText">
                  <div class="cst__name">
                    {{ nome(row) }}
                  </div>
                  <div class="cst__meta">
                    {{ row.mail }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- Auto del cliente: le targhe, al massimo due più
               il conteggio delle altre. Il valet è legato all'auto, e la
               targa è quello che il dipendente ha davanti. -->
          <el-table-column
            label="Auto"
            min-width="190"
          >
            <template #default="{ row }">
              <div
                v-if="row.cars && row.cars.length"
                class="cst__plates"
              >
                <span
                  v-for="c in row.cars.slice(0, 2)"
                  :key="c.id"
                  class="cst__plate"
                  :title="[c.marca, c.modello].filter(Boolean).join(' ')"
                >{{ c.targa || '—' }}</span>
                <span
                  v-if="row.cars.length > 2"
                  class="cst__meta cst__num"
                >+{{ row.cars.length - 2 }}</span>
              </div>
              <span
                v-else
                class="cst__dash"
              >—</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Telefono"
            min-width="150"
          >
            <template #default="{ row }">
              <span class="cst__num">{{ row.telephone_number || '—' }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Città"
            min-width="140"
          >
            <template #default="{ row }">
              {{ row.city || '—' }}
            </template>
          </el-table-column>

          <el-table-column
            label="Pacchetto valet"
            min-width="230"
          >
            <template #default="{ row }">
              <template v-if="riepiloghi[row.id]">
                <StatusPill
                  :kind="riepiloghi[row.id].kind"
                  :label="riepiloghi[row.id].label"
                />
                <span
                  v-if="riepiloghi[row.id].residui !== null"
                  class="cst__meta cst__num cst__residuiTxt"
                >{{ riepiloghi[row.id].residui }} residui</span>
              </template>
              <span
                v-else
                class="cst__dash"
              >—</span>
            </template>
          </el-table-column>
        </template>

        <!-- ==== PACCHETTI (da confermare / confermati) ==== -->
        <template v-else>
          <el-table-column
            label="Cliente"
            min-width="240"
          >
            <template #default="{ row }">
              <div class="cst__who">
                <span class="cst__avatar">{{ iniziali(row.car && row.car.customer) }}</span>
                <div class="cst__whoText">
                  <div class="cst__name">
                    {{ nome(row.car && row.car.customer) }}
                  </div>
                  <div class="cst__meta">
                    {{ (row.car && row.car.customer && row.car.customer.mail) || '' }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Auto"
            min-width="170"
          >
            <template #default="{ row }">
              <div>{{ auto(row) || '—' }}</div>
              <span
                v-if="row.car && row.car.targa"
                class="cst__plate"
              >{{ row.car.targa }}</span>
            </template>
          </el-table-column>

          <!-- colonne dei pacchetti valet -->
          <template v-if="segmento === 'attesa'">
            <el-table-column
              label="Valet pickup"
              align="right"
              width="90"
            >
              <template #default="{ row }">
                <span class="cst__num cst__strong">{{ row.valet }}</span>
              </template>
            </el-table-column>

            <!-- Prezzo AL CLIENTE, con le tariffe del partner: `imponibile`
                 sul pacchetto è il prezzo Simba e non si mostra. -->
            <el-table-column
              label="Al cliente"
              align="right"
              width="150"
            >
              <template #default="{ row }">
                <span class="cst__num">{{ prezzoCliente(row) === null ? '—' : `${euro(prezzoCliente(row))} + IVA` }}</span>
              </template>
            </el-table-column>

            <el-table-column
              label="Richiesto il"
              width="120"
            >
              <template #default="{ row }">
                <span class="cst__num">{{ dataCorta(row.data_reg) }}</span>
              </template>
            </el-table-column>
          </template>

          <!-- confermati -->
          <template v-else>
            <el-table-column
              label="Residui"
              align="right"
              width="170"
            >
              <template #default="{ row }">
                <div class="cst__res">
                  <span class="cst__num cst__strong">{{ Number(row.residual_valet) || 0 }}</span>
                  <span class="cst__meta cst__num">/ {{ Number(row.valet) || 0 }}</span>
                  <span class="cst__resBar">
                    <span
                      class="cst__resFill"
                      :class="{ 'is-low': percentuale(row) <= 20 }"
                      :style="{ width: `${percentuale(row)}%` }"
                    />
                  </span>
                </div>
              </template>
            </el-table-column>

            <el-table-column
              label="Scadenza"
              width="120"
            >
              <template #default="{ row }">
                <span class="cst__num">{{ dataLunga(row.expiration_valet) }}</span>
              </template>
            </el-table-column>
          </template>

          <el-table-column
            label="Stato"
            min-width="170"
          >
            <template #default="{ row }">
              <StatusPill
                :kind="statoPacchetto(row).kind"
                :label="statoPacchetto(row).label"
              />
            </template>
          </el-table-column>
        </template>

        <!-- ==== azione (comune) ==== -->
        <el-table-column
          width="150"
          align="right"
        >
          <template #default="{ row }">
            <!-- Niente «Reinvia email»: i pacchetti in attesa li accetta Simba,
                 al cliente non si manda nulla. -->
            <el-button
              size="small"
              @click.stop="apri(row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="cst__foot">
        <span class="cst__footInfo">{{ testoPiede }}</span>
        <div class="cst__spacer" />
        <el-pagination
          v-if="totale > PAGE_SIZE"
          background
          layout="prev, pager, next"
          :page-size="PAGE_SIZE"
          :total="totale"
          :current-page="pagina"
          @current-change="cambiaPagina"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import StatusPill from '@/Components/ui/StatusPill.vue';
import partnerAPI from '@/services/partnerAPI';
import { isLoggedStore } from '@/store/logged';
import {
  PAGE_SIZE,
  clientiPagina,
  contaClienti,
  pacchettiPagina,
  contaPacchetti,
  pacchettiDeiClienti,
  tuttiIClienti,
  statoPacchetto,
  riepilogoValetCliente,
  listinoValetPartner,
  prezzoClientePacchetto,
} from '@/services/clientiAPI';

const SEGMENTI = {
  clienti: {
    titolo: 'Tutti i clienti',
    nota: 'Ordinati per cognome',
    cerca: 'Cerca nome, targa, mail, telefono, città…',
    parola: (n) => (n === 1 ? 'cliente' : 'clienti'),
    vuoto: 'Nessun cliente corrisponde alla ricerca',
  },
  aziende: {
    titolo: 'Aziende',
    nota: 'Clienti con ragione sociale',
    cerca: 'Cerca ragione sociale, targa, mail, telefono…',
    parola: (n) => (n === 1 ? 'azienda' : 'aziende'),
    vuoto: 'Nessuna azienda corrisponde alla ricerca',
  },
  attesa: {
    titolo: 'Valet pickup',
    nota: 'Tutti i pacchetti del partner, dal più recente',
    cerca: 'Cerca targa, auto o cliente…',
    parola: (n) => (n === 1 ? 'pacchetto' : 'pacchetti'),
    vuoto: 'Nessun pacchetto valet',
  },
};

export default {
  name: 'CustomerPage',
  components: { StatusPill },

  setup() {
    const router = useRouter();
    const storeLogin = isLoggedStore();

    const segmento = ref('clienti');
    const righe = ref([]);
    const totale = ref(0);
    const pagina = ref(1);
    const cerca = ref('');
    const caricamento = ref(false);
    const errore = ref('');
    const esporto = ref(false);
    const riepiloghi = ref({});
    const conteggi = reactive({ clienti: null, aziende: null, valet: null, attesa: null });

    // -------------------------------------------------------------------
    //  Il partner. Arriva dallo store al login; la chiamata è un ripiego per
    //  le sessioni in cui lo store non lo contiene.
    // -------------------------------------------------------------------
    let partnerId = '';
    const risolviPartner = async () => {
      if (partnerId) return partnerId;
      partnerId = storeLogin.partner?.id || '';
      if (!partnerId && storeLogin.employee?.id) {
        const r = await partnerAPI.getPartnerByEmployee(storeLogin.employee.id);
        partnerId = r?.data?.[0]?.headquarters?.[0]?.partner?.id || '';
      }
      if (!partnerId) {
        throw new Error('Il tuo utente non risulta associato a nessuna sede: non posso caricare i clienti.');
      }
      return partnerId;
    };

    const messaggioErrore = (e) => {
      if (e?.message && !e.response && !e.request) return e.message;
      if (!e?.response) return 'Non riesco a raggiungere il server.';
      return 'Non sono riuscito a caricare i dati.';
    };

    // -------------------------------------------------------------------
    //  Caricamento della pagina corrente.
    //
    //  ⚠️ «L'ultima richiesta vince». Con la ricerca che interroga il server
    //  mentre si scrive, due risposte possono arrivare in ordine inverso:
    //  senza questo contatore la tabella mostrerebbe i risultati di una
    //  ricerca gia' superata.
    // -------------------------------------------------------------------
    let ultima = 0;
    // Listino valet del partner, per il prezzo al cliente dei pacchetti.
    const fasceValet = ref([]);
    const prezzoCliente = (m) => prezzoClientePacchetto(m, fasceValet.value);

    const carica = async () => {
      const mia = ++ultima;
      caricamento.value = true;
      errore.value = '';
      try {
        const pid = await risolviPartner();
        const res = segmento.value === 'attesa'
          // `confermati: null` = tutti i pacchetti, dal più recente.
          ? await pacchettiPagina({
            partnerId: pid,
            confermati: null,
            page: pagina.value,
            cerca: cerca.value,
          })
          : await clientiPagina({
            partnerId: pid,
            page: pagina.value,
            cerca: cerca.value,
            tipo: segmento.value,
          });
        if (mia !== ultima) return;
        if (segmento.value === 'attesa' && !fasceValet.value.length) {
          try {
            fasceValet.value = await listinoValetPartner(pid);
          } catch (e) {
            console.warn('Listino valet non letto:', e?.response?.status);
          }
          if (mia !== ultima) return;
        }
        righe.value = res.rows;
        totale.value = res.total;

        if (segmento.value !== 'attesa') {
          // La colonna «Pacchetto valet»: una richiesta per i soli clienti
          // di questa pagina.
          const pacchetti = await pacchettiDeiClienti(pid, res.rows.map((c) => c.id));
          if (mia !== ultima) return;
          const perCliente = {};
          pacchetti.forEach((m) => {
            const id = m?.car?.customer?.id;
            if (id) (perCliente[id] = perCliente[id] || []).push(m);
          });
          const mappa = {};
          Object.keys(perCliente).forEach((id) => {
            mappa[id] = riepilogoValetCliente(perCliente[id]);
          });
          riepiloghi.value = mappa;
        }
      } catch (e) {
        if (mia !== ultima) return;
        righe.value = [];
        totale.value = 0;
        errore.value = messaggioErrore(e);
      } finally {
        if (mia === ultima) caricamento.value = false;
      }
    };

    // I conteggi dei riquadri: separati, cosi' si vedono anche i segmenti
    // che non si stanno guardando (es. che ci sono pacchetti in attesa).
    const caricaConteggi = async () => {
      let pid;
      try {
        pid = await risolviPartner();
      } catch (e) {
        return; // l'errore lo mostra gia' carica()
      }
      const [c, az, v, a] = await Promise.allSettled([
        contaClienti(pid, 'clienti'),
        contaClienti(pid, 'aziende'),
        contaPacchetti(pid, null),
        contaPacchetti(pid, false),
      ]);
      conteggi.clienti = c.status === 'fulfilled' ? c.value : null;
      conteggi.aziende = az.status === 'fulfilled' ? az.value : null;
      conteggi.valet = v.status === 'fulfilled' ? v.value : null;
      conteggi.attesa = a.status === 'fulfilled' ? a.value : null;
    };

    const ricarica = () => {
      carica();
      caricaConteggi();
    };

    const scegli = (key) => {
      if (segmento.value === key) return;
      segmento.value = key;
      pagina.value = 1;
      righe.value = [];
      carica();
    };

    let timerCerca = null;
    const onCerca = () => {
      clearTimeout(timerCerca);
      timerCerca = setTimeout(() => {
        pagina.value = 1;
        carica();
      }, 350);
    };

    const cambiaPagina = (p) => {
      pagina.value = p;
      carica();
    };

    const apri = (row) => {
      const id = segmento.value === 'attesa' ? row?.car?.customer?.id : row?.id;
      if (id) router.push(`/details/customerAlive/${id}`);
    };

    // -------------------------------------------------------------------
    //  Excel dell'anagrafica: chiavi delle colonne e campi delle righe devono
    //  combaciare (`telephone_number`).
    // -------------------------------------------------------------------
    const scaricaExcel = async () => {
      esporto.value = true;
      try {
        const pid = await risolviPartner();
        const { rows, truncated } = await tuttiIClienti(pid);
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Clienti');
        ws.columns = [
          { header: 'ID cliente', key: 'id', width: 38 },
          { header: 'Nome', key: 'nome', width: 18 },
          { header: 'Cognome', key: 'cognome', width: 20 },
          { header: 'Ragione sociale', key: 'azienda', width: 26 },
          { header: 'Mail', key: 'mail', width: 32 },
          { header: 'Telefono', key: 'telefono', width: 16 },
          { header: 'Città', key: 'citta', width: 18 },
        ];
        rows.forEach((c) => ws.addRow({
          id: c.id,
          nome: c.name || '',
          cognome: c.surname || '',
          azienda: c.company_name || '',
          mail: c.mail || '',
          telefono: c.telephone_number || '',
          citta: c.city || '',
        }));
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clienti-${dayjs().format('YYYY-MM-DD')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        if (truncated) {
          ElMessage({
            type: 'warning',
            message: 'Il server ha restituito solo una parte dei clienti: il file è incompleto.',
          });
        }
      } catch (e) {
        ElMessage({ type: 'error', message: 'Non sono riuscito a preparare il file Excel.' });
      } finally {
        esporto.value = false;
      }
    };

    // ---- testi e riquadri ------------------------------------------------
    const riquadri = computed(() => [
      { key: 'clienti', label: 'Clienti', hint: 'privati', n: conteggi.clienti, tono: 'neutro' },
      { key: 'aziende', label: 'Aziende', hint: 'con ragione sociale', n: conteggi.aziende, tono: 'neutro' },
      // Tutti i pacchetti del partner, dal più recente; accanto, piccoli,
      // quanti aspettano ancora la conferma di Simba.
      {
        key: 'attesa',
        label: 'Valet',
        hint: conteggi.attesa ? 'con pacchetti in attesa' : 'tutti confermati',
        n: conteggi.valet,
        badge: conteggi.attesa ? `${conteggi.attesa} da confermare` : '',
        tono: conteggi.attesa ? 'attn' : 'neutro',
      },
    ]);

    const titoloTabella = computed(() => SEGMENTI[segmento.value].titolo);
    const notaTabella = computed(() => SEGMENTI[segmento.value].nota);
    const segnaposto = computed(() => SEGMENTI[segmento.value].cerca);
    const testoVuoto = computed(() => (cerca.value.trim()
      ? SEGMENTI[segmento.value].vuoto
      : SEGMENTI[segmento.value].vuoto.replace(' corrisponde alla ricerca', '')));
    const testoPiede = computed(() => {
      const s = SEGMENTI[segmento.value];
      const pagine = Math.max(1, Math.ceil(totale.value / PAGE_SIZE));
      const base = `${totale.value} ${s.parola(totale.value)}`;
      return pagine > 1 ? `${base} · pagina ${pagina.value} di ${pagine}` : base;
    });

    // ---- presentazione ---------------------------------------------------
    const nome = (c) => c?.company_name || [c?.name, c?.surname].filter(Boolean).join(' ') || '—';

    const iniziali = (c) => {
      const src = (c?.company_name || `${c?.name || ''} ${c?.surname || ''}`).trim();
      return src.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
    };

    const auto = (m) => [m?.car?.marca, m?.car?.modello].filter(Boolean).join(' ');

    const euro = (v) => (v === null || v === undefined || v === ''
      ? '—'
      : `€ ${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

    const dataCorta = (v) => (v ? dayjs(v).format('DD/MM') : '—');
    const dataLunga = (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—');

    // Barra dei residui: su quanti valet ha il pacchetto. Limitata a 100%
    // nel caso (anomalo) di residui superiori al totale.
    const percentuale = (m) => {
      const tot = Number(m?.valet) || 0;
      const res = Number(m?.residual_valet) || 0;
      return tot > 0 ? Math.min(100, Math.round((res / tot) * 100)) : 0;
    };

    onMounted(() => {
      carica();
      caricaConteggi();
    });

    return {
      PAGE_SIZE,
      segmento,
      righe,
      totale,
      pagina,
      cerca,
      caricamento,
      errore,
      esporto,
      riepiloghi,
      riquadri,
      titoloTabella,
      notaTabella,
      segnaposto,
      testoVuoto,
      testoPiede,
      scegli,
      onCerca,
      cambiaPagina,
      apri,
      prezzoCliente,
      ricarica,
      scaricaExcel,
      statoPacchetto,
      nome,
      iniziali,
      auto,
      euro,
      dataCorta,
      dataLunga,
      percentuale,
    };
  },
};
</script>

<style scoped>
.cst {
  padding: 4px 8px 32px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cst__spacer { flex: 1 1 auto; }

/* ---- intestazione ---- */
.cst__head {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}
.cst__title {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-primary);
}
.cst__sub {
  margin: 3px 0 0;
  font-size: 13px;
  color: var(--color-text-secondary);
}
.cst__search {
  width: 290px;
  max-width: 100%;
}

/* ---- riquadri: contano e filtrano ---- */
.cst__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 760px) {
  .cst__kpis { grid-template-columns: 1fr; }
}
.cst__kpi {
  text-align: left;
  font: inherit;
  cursor: pointer;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 13px 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 2px 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cst__kpi:hover { border-color: var(--color-primary); }
.cst__kpi.is-on {
  border-color: var(--color-primary);
  box-shadow: inset 0 0 0 1px var(--color-primary);
}
.cst__kpi:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}
.cst__kpiN {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-primary);
}
.cst__kpiB {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--state-attn);
  white-space: nowrap;
}
.cst__kpi--attn .cst__kpiN { color: var(--state-attn); }
.cst__kpi--ok .cst__kpiN { color: var(--state-ok); }
.cst__kpiL {
  grid-column: 1 / 2;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}
.cst__kpiH {
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  align-self: center;
  max-width: 16ch;
  text-align: right;
  font-size: 11.5px;
  color: var(--color-text-muted);
}
@media (prefers-reduced-motion: reduce) {
  .cst__kpi { transition: none; }
}

/* ---- tabella ---- */
.cst__card {
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
}
.cst__bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 11px 14px;
  border-bottom: 1px solid var(--color-border);
}
.cst__barT { font-size: 13.5px; font-weight: 650; }
.cst__barH { font-size: 12px; color: var(--color-text-muted); }

.cst__errore {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--state-danger);
  background: var(--state-danger-bg);
  border-bottom: 1px solid var(--state-danger-line);
}
.cst__riprova {
  font: inherit;
  font-size: 12px;
  color: var(--state-danger);
  background: #fff;
  border: 1px solid var(--state-danger-line);
  border-radius: 5px;
  padding: 2px 9px;
  cursor: pointer;
}

.cst__who { display: flex; align-items: center; gap: 10px; min-width: 0; }
.cst__whoText { min-width: 0; }
.cst__avatar {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #232c51;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.cst__name { font-weight: 600; color: var(--color-text-primary); }
.cst__meta { font-size: 12px; color: var(--color-text-muted); }
.cst__num { font-variant-numeric: tabular-nums; }
.cst__strong { font-weight: 700; }
.cst__dash { color: var(--color-text-muted); }
.cst__plates { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 6px; }
.cst__residuiTxt { margin-left: 8px; }
.cst__plate {
  display: inline-block;
  margin-top: 3px;
  padding: 0 6px;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  background: var(--color-bg-secondary);
  font-size: 11.5px;
  letter-spacing: 0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
}

/* residui: numero e barra proporzionale ai valet del pacchetto */
.cst__res { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.cst__resBar {
  width: 58px;
  height: 6px;
  border-radius: 3px;
  background: var(--state-muted-bg);
  overflow: hidden;
}
.cst__resFill {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--state-ok);
}
.cst__resFill.is-low { background: var(--state-attn); }

/* ---- piede ---- */
.cst__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 11px 14px;
  border-top: 1px solid var(--color-border);
}
.cst__footInfo { font-size: 12.5px; color: var(--color-text-muted); }

/* La riga si apre con un click: lo si deve capire. */
.cst :deep(.el-table__row) { cursor: pointer; }

/* ---- intestazione della tabella ----
   `global.css` ed `element-overrides.css` impongono a TUTTE le tabelle
   un'intestazione viola chiaro con `!important`. Su un elenco denso ruba
   attenzione ai dati: qui torna al grigio chiaro, come nell'admin. Serve
   `!important` per vincere sugli override globali, e la modifica e' LOCALE a
   questa pagina. */
.cst :deep(.el-table .el-table__header-wrapper th.el-table__cell) {
  background: var(--color-bg-secondary) !important;
  border-bottom: 1px solid var(--color-border) !important;
}
.cst :deep(.el-table .el-table__header-wrapper th.el-table__cell .cell) {
  color: var(--color-text-secondary) !important;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
</style>
